"use client";

import { useRouter, usePathname } from "next/navigation";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { AuthContextType, AuthResponse, ICredentials } from "types/common";
import setedigitalApi from "../../services/api/setedigitalApi";
import { toaster } from "../../components/ui/toaster";
import { CustomDialog } from "components/ui/dialog";
import { VStack, Text, Button } from "@chakra-ui/react";
import { Info } from "lucide-react";
import {
  notifyAuthRestore,
  notifyAuthSuccess,
  notifyLogout,
} from "../../utils/reactNativeWebViewBridge";

const AuthContext = createContext({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [user, setUser] = useState<AuthResponse>();
  const [currentPath, setCurrentPath] = useState<any>(pathname);
  const [titlePage, setTitlePage] = useState<string>("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  const clearStoredAuth = () => {
    const hasStoredToken = localStorage.getItem("token");

    if (hasStoredToken) {
      notifyLogout();
    }

    localStorage.removeItem("token");
    localStorage.clear();
  };

  // const login = async (
  //   credentials: ICredentials,
  //   hadTemporaryPassword?: boolean,
  // ) => {
  const login = async (
    credentials: ICredentials & {
      redirectTo?: string;
    },
    hadTemporaryPassword?: boolean,
  ) => {
  try {
      setIsLoading(true);
      const res = await setedigitalApi.post("/auth/authenticate", credentials);

      if (res.data?.mustUpdateEmail) {
        toaster.create({
          title: "Atualização de e-mail necessária",
          description:
            "Para continuar, você precisa atualizar seu e-mail funcional (@mpsp.mp.br).",
          type: "warning",
          duration: 8000,
        });

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("userId", res.data.user._id);
        localStorage.setItem("username", res.data.user.username);
        localStorage.setItem("mustUpdateEmail", "true");

        setUser(res.data.user);
        setIsAuthenticated(true);
        notifyAuthSuccess(res.data.token, res.data.user);

        window.location.href = res.data.redirectTo || "/email/enviar";
        setIsLoading(false);
        return;
      }

      if (res.data.mustChangePassword) {
        toaster.create({
          title: "Redefina sua senha",
          description: "Sua senha é temporária. Por favor, redefina para continuar.",
          type: "warning",
          duration: 7000,
        });
        localStorage.setItem("mustChangePassword", "true");
        window.location.href = "/conta/esqueci-minha-senha";
        setIsLoading(false);
        return;
      }

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("userId", res.data.user._id);
      localStorage.setItem("username", res.data.user.username);
      setUser(res.data.user);

      const resData = await setedigitalApi.get("/associate/me?fields=consultCode");
      localStorage.setItem("consultCode", resData.data.consultCode);

      setIsAuthenticated(true);
      notifyAuthSuccess(res.data.token, res.data.user);

      if (hadTemporaryPassword) {
        localStorage.setItem("showCustomDialog", "true");
        // window.location.href = "/";
        window.location.href = credentials.redirectTo || "/";
      } else {
        // window.location.href = "/";
        window.location.href = credentials.redirectTo || "/";
      }

      toaster.create({
        title: "Sucesso",
        type: "success",
        description: `Bem vindo, ${res.data.user.name.split(" ")[0]}`,
        duration: 5000,
      });
    } catch (error: any) {
      console.log("Erro no login:", error);

      if (error.response?.status === 403) {

        if (error.response.data?.code === "MUST_CHANGE_PASSWORD") {
          toaster.create({
            title: "Redefina sua senha",
            description: error.response.data.message,
            type: "warning",
            duration: 7000,
          });

          localStorage.setItem("mustChangePassword", "true");

          window.location.href =
            error.response.data.redirectTo || "/conta/esqueci-minha-senha";

          setIsLoading(false);
          return;
        }

        if (error.response.data?.code === "MUST_UPDATE_EMAIL") {
          const { token, user } = error.response.data;

          toaster.create({
            title: "Atualização necessária",
            description: error.response.data.message,
            type: "warning",
            duration: 7000,
          });

          localStorage.setItem("token", token);
          localStorage.setItem("userId", user._id);
          localStorage.setItem("username", user.username);
          localStorage.setItem("mustUpdateEmail", "true");

          setUser(user);
          setIsAuthenticated(true);
          notifyAuthSuccess(token, user);

          window.location.href = "/email/enviar";
          setIsLoading(false);
          return;
        }

        if (error.response.data?.mustVerifyEmail) {
          toaster.create({
            title: "Verifique seu Email",
            description:
              "Seu e-mail não foi verificado. Solicite o envio do email de solicitação de cadastro para validar o seu acesso.",
            type: "warning",
            duration: 7000,
          });

          localStorage.setItem("mustVerifyEmail", "true");
          window.location.href = "/conta/reenviar-verificacao";
          setIsLoading(false);
          return;
        }

        if (error.response.data?.error === "User is inactive") {
          toaster.create({
            title: "Conta inativa",
            description:
              "Sua conta está inativa. Entre em contato com o administrador para liberar seu acesso.",
            type: "warning",
            duration: 7000,
          });
          setIsLoading(false);
          return;
        }
      }

      toaster.create({
        title: "Erro ao fazer login",
        description: "Algo deu errado. Tente novamente.",
        type: "error",
        duration: 5000,
      });

      const desc =
        error?.response?.status === 401
          ? "Usuário ou senha inválidos"
          : "Operação não permitida";
      toaster.create({
        title: "Atenção",
        type: "error",
        description: desc,
        duration: 9000,
      });

      setIsAuthenticated(false);
      clearStoredAuth();
      setUser(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      clearStoredAuth();
      setUser(undefined);
      setIsAuthenticated(false);
      router.push("/conta/login");
    } catch (error: any) {
      toaster.create({
        title: "Erro ao sair",
        description: "Ocorreu um erro ao tentar fazer logout. Tenta de novo!",
        type: "error",
        duration: 5000,
      });
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      setIsLoading(true);
      const response = await setedigitalApi.post("/associate/resend-verification", { email });

      if (response.status === 200) {
        toaster.create({
          title: "E-mail enviado",
          description: "Um novo link de verificação foi enviado para seu e-mail.",
          type: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      toaster.create({
        title: "Erro",
        description: "Não foi possível enviar o e-mail. Tente novamente.",
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // const validateToken = async (token: string) => {
  //   const response = await setedigitalApi.post("/auth/validate", { token }); // Ajuste a rota aqui

  //   if (response.status === 200) {
  //     setIsAuthenticated(true);
  //   } else {
  //     setIsAuthenticated(false);
  //     localStorage.removeItem("token");
  //     localStorage.clear();
  //   }
  // };

  // useEffect(() => {
  //   const hasToken = localStorage.getItem("token");

  //   if (isAuthenticated) {
  //     router.push(currentPath);
  //   }

  //   // if (hasToken) {
  //   //   validateToken(hasToken);
  //   // } else {
  //   //   setIsAuthenticated(false);
  //   // }

  //   // if (!isAuthenticated) {
  //   //   router.push("/login");
  //   // }

  //   if (
  //     isAuthenticated &&
  //     (!currentPath.includes("login") || !currentPath.includes("cadastro"))
  //   ) {
  //     router.push(currentPath);
  //   }

  //   // TODO: Cada reload ele redireciona para o /dashboard
  //   if (hasToken && currentPath === "/") {
  //     setCurrentPath("/");
  //     // router.push("/");
  //   }
  // }, [isAuthenticated]);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      notifyAuthRestore(storedToken);
    }
  }, []);

  useEffect(() => {
    const mustUpdateEmail = localStorage.getItem("mustUpdateEmail");

    if (isAuthenticated && mustUpdateEmail !== "true") {
      router.replace("/");
    }
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        resendVerificationEmail,
        isLoading,
        user,
        setUser,
        setIsLoading,
        titlePage,
        setTitlePage,
        showCustomDialog,
        setShowCustomDialog,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
